import { useMemo } from 'react';

import { TagModel, WorklistViewModel } from 'models';

import { PreparedFile } from 'features/patient/fragments/PatientFiles.types';

import { extractWorklistTagIds, findChildWorklists } from '../services/worklistNavigationService';

interface GroupMetadata {
  key: string;
  displayName: string;
  worklistId?: number;
  worklistName?: string;
}

type GroupedFiles = Map<string, PreparedFile[]>;
type GroupedMetadata = Map<string, GroupMetadata>;

export const useFileGrouping = (
  finalFilteredAndSortedFiles: PreparedFile[],
  selectedWorklist: WorklistViewModel | null,
  allWorklists: WorklistViewModel[] | null,
  mergedHierarchicalTags: TagModel[] | null,
  mergedTagNameToIdMap: Map<string, number>,
): { groupedFiles: GroupedFiles; groupMetadata: GroupedMetadata } => {
  const result = useMemo<{ groupedFiles: GroupedFiles; groupMetadata: GroupedMetadata }>(() => {
    const groups: GroupedFiles = new Map();
    const metadata: GroupedMetadata = new Map();

    if (!finalFilteredAndSortedFiles || finalFilteredAndSortedFiles.length === 0) {
      return { groupedFiles: groups, groupMetadata: metadata };
    }

    // First, create a Notes & AI Activity group from all files
    const noteFiles = finalFilteredAndSortedFiles.filter((file) => {
      // Text notes typically have .txt extension and contain "note" in filename
      const isTextNote = file.fileName?.toLowerCase().includes('note') && file.fileType?.toLowerCase() === 'text/plain';

      // Audio notes are audio files with note-related tags or names
      const isAudioNote = file.fileType?.startsWith('audio/') && file.fileName?.toLowerCase().includes('note');

      // AI summaries and reports (check both categoryId and filename)
      const isAISummary =
        file.fileName?.toLowerCase().includes('ai summary') ||
        file.fileName?.toLowerCase().includes('aisummary') ||
        file.description?.toLowerCase().includes('ai summary');

      return isTextNote || isAudioNote || isAISummary;
    });

    if (noteFiles.length > 0) {
      const notesKey = 'notes-ai-activity';
      groups.set(notesKey, noteFiles);
      metadata.set(notesKey, {
        key: notesKey,
        displayName: '📝 Notes & AI Activity',
      });
    }

    // Special handling for "All Files" view - group by actual worklists
    if (selectedWorklist?.id === -1 && allWorklists && mergedHierarchicalTags) {
      console.log('[Grouping] Using All Files view - grouping by worklists');

      // Get only top-level parent worklists (excluding the All Files system worklist and child worklists)
      const realWorklists = allWorklists.filter((w) => w.id !== -1 && w.oDataResourceString && w.parentViewId === null);

      // Create groups for each parent worklist (including files from child worklists)
      realWorklists.forEach((parentWorklist) => {
        const groupKey = `worklist-${parentWorklist.id}`;
        const filesForWorklist: PreparedFile[] = [];

        // Get all descendant worklists (children, grandchildren, etc.)
        const getAllDescendantWorklists = (parent: WorklistViewModel): WorklistViewModel[] => {
          const descendants: WorklistViewModel[] = [parent];
          const children = allWorklists.filter((w) => w.parentViewId === parent.id);
          children.forEach((child) => {
            descendants.push(...getAllDescendantWorklists(child));
          });
          return descendants;
        };

        const allRelatedWorklists = getAllDescendantWorklists(parentWorklist);

        // Collect all tag IDs from parent and descendant worklists
        const allWorklistTagIds = new Set<number>();
        allRelatedWorklists.forEach((worklist) => {
          const tagIds = extractWorklistTagIds(worklist);
          tagIds.forEach((id) => allWorklistTagIds.add(id));
        });

        if (allWorklistTagIds.size > 0) {
          finalFilteredAndSortedFiles.forEach((file) => {
            const fileTagIds = new Set<number>(file.tagIds || []);

            // Add derived tag IDs
            file.derivedTagNames?.forEach((name) => {
              const derivedId = mergedTagNameToIdMap.get(name.toLowerCase());
              if (derivedId !== undefined) {
                fileTagIds.add(derivedId);
              }
            });

            // Check if file belongs to this parent worklist or any of its descendants
            if (Array.from(allWorklistTagIds).some((tagId) => fileTagIds.has(tagId))) {
              filesForWorklist.push(file); // preserve sort order from useFileFiltering
            }
          });
        }

        // Always create the group for the worklist, even if it has no files (show as empty folder)
        groups.set(groupKey, filesForWorklist);
        metadata.set(groupKey, {
          key: groupKey,
          displayName: parentWorklist.name,
          worklistId: parentWorklist.id,
          worklistName: parentWorklist.name,
        });
      });

      // Add any files that don't belong to any parent worklist (including their descendants) as "Uncategorized"
      const allWorklistTagIds = new Set<number>();
      realWorklists.forEach((parentWorklist) => {
        // Get all descendant worklists for this parent
        const getAllDescendantWorklists = (parent: WorklistViewModel): WorklistViewModel[] => {
          const descendants: WorklistViewModel[] = [parent];
          const children = allWorklists.filter((w) => w.parentViewId === parent.id);
          children.forEach((child) => {
            descendants.push(...getAllDescendantWorklists(child));
          });
          return descendants;
        };

        const allRelatedWorklists = getAllDescendantWorklists(parentWorklist);
        allRelatedWorklists.forEach((worklist) => {
          const worklistTagIds = extractWorklistTagIds(worklist);
          worklistTagIds.forEach((id) => allWorklistTagIds.add(id));
        });
      });

      const uncategorizedFiles: PreparedFile[] = [];
      finalFilteredAndSortedFiles.forEach((file) => {
        const fileTagIds = new Set<number>(file.tagIds || []);

        // Add derived tag IDs
        file.derivedTagNames?.forEach((name) => {
          const derivedId = mergedTagNameToIdMap.get(name.toLowerCase());
          if (derivedId !== undefined) {
            fileTagIds.add(derivedId);
          }
        });

        // Check if file doesn't belong to any worklist
        if (!Array.from(fileTagIds).some((tagId) => allWorklistTagIds.has(tagId))) {
          uncategorizedFiles.push(file); // preserve sort order from useFileFiltering
        }
      });

      if (uncategorizedFiles.length > 0) {
        const uncategorizedKey = 'uncategorized-files';
        groups.set(uncategorizedKey, uncategorizedFiles);
        metadata.set(uncategorizedKey, {
          key: uncategorizedKey,
          displayName: 'Uncategorized Files',
        });
      }

      console.log('[Grouping] All Files grouping result:', {
        totalGroups: groups.size,
        groupKeys: Array.from(groups.keys()),
        groupSizes: Array.from(groups.entries()).map(([key, files]) => ({ key, count: files.length })),
      });

      return { groupedFiles: groups, groupMetadata: metadata };
    }

    // If we have a selected worklist (not All Files), try to group according to its structure and child worklists
    if (selectedWorklist?.oDataResourceString && allWorklists && mergedHierarchicalTags) {
      const worklistTagIds = extractWorklistTagIds(selectedWorklist);

      if (worklistTagIds.length > 0) {
        console.log('[Grouping] Using worklist-based grouping for tags:', worklistTagIds);

        // Find child worklists using the simplified navigation service
        const childWorklists = findChildWorklists(selectedWorklist, allWorklists);

        console.log(
          '[Grouping] Found child worklists:',
          childWorklists.map((w) => w.name),
        );

        // If we have child worklists, group by them
        if (childWorklists.length > 0) {
          // Create groups for each child worklist
          childWorklists.forEach((childWorklist) => {
            const childWorklistTagIds = extractWorklistTagIds(childWorklist);
            const groupKey = `child-worklist-${childWorklist.id}`;
            const filesForChild: PreparedFile[] = [];

            finalFilteredAndSortedFiles.forEach((file) => {
              const fileTagIds = new Set<number>(file.tagIds || []);

              // Add derived tag IDs
              file.derivedTagNames?.forEach((name) => {
                const derivedId = mergedTagNameToIdMap.get(name.toLowerCase());
                if (derivedId !== undefined) {
                  fileTagIds.add(derivedId);
                }
              });

              // Check if file belongs to this child worklist - simplified logic
              // Just check if any file tag matches any worklist tag
              if (childWorklistTagIds.some((tagId) => fileTagIds.has(tagId))) {
                filesForChild.push(file); // preserve sort order from useFileFiltering
              }
            });

            if (filesForChild.length > 0) {
              groups.set(groupKey, filesForChild);
              metadata.set(groupKey, {
                key: groupKey,
                displayName: childWorklist.name,
                worklistId: childWorklist.id,
                worklistName: childWorklist.name,
              });
            }
          });

          // Add any remaining files that don't belong to child worklists
          const allChildTagIds = new Set<number>();
          childWorklists.forEach((childWorklist) => {
            const childTagIds = extractWorklistTagIds(childWorklist);
            childTagIds.forEach((id) => allChildTagIds.add(id));
          });

          const unassignedFiles: PreparedFile[] = [];
          finalFilteredAndSortedFiles.forEach((file) => {
            const fileTagIds = new Set<number>(file.tagIds || []);

            // Add derived tag IDs
            file.derivedTagNames?.forEach((name) => {
              const derivedId = mergedTagNameToIdMap.get(name.toLowerCase());
              if (derivedId !== undefined) {
                fileTagIds.add(derivedId);
              }
            });

            // Check if file doesn't belong to any child worklist
            if (!Array.from(fileTagIds).some((tagId) => allChildTagIds.has(tagId))) {
              unassignedFiles.push(file); // preserve sort order from useFileFiltering
            }
          });

          if (unassignedFiles.length > 0) {
            const unassignedKey = 'unassigned-files';
            groups.set(unassignedKey, unassignedFiles);
            metadata.set(unassignedKey, {
              key: unassignedKey,
              displayName: `Other ${selectedWorklist.name} Files`,
            });
          }

          console.log('[Grouping] Final grouping result:', {
            totalGroups: groups.size,
            groupKeys: Array.from(groups.keys()),
            groupSizes: Array.from(groups.entries()).map(([key, files]) => ({ key, count: files.length })),
          });

          return { groupedFiles: groups, groupMetadata: metadata };
        }

        // If no child worklists, create a single group for the selected worklist
        const singleGroupKey = `worklist-${selectedWorklist.id}`;
        groups.set(singleGroupKey, [...finalFilteredAndSortedFiles]); // preserve sort order from useFileFiltering
        metadata.set(singleGroupKey, {
          key: singleGroupKey,
          displayName: selectedWorklist.name,
          worklistId: selectedWorklist.id,
          worklistName: selectedWorklist.name,
        });

        return { groupedFiles: groups, groupMetadata: metadata };
      }
    }

    // Default grouping: all files in one group
    const defaultKey = 'all-files';
    groups.set(defaultKey, [...finalFilteredAndSortedFiles]); // preserve sort order from useFileFiltering
    metadata.set(defaultKey, {
      key: defaultKey,
      displayName: 'All Files',
    });

    return { groupedFiles: groups, groupMetadata: metadata };
  }, [finalFilteredAndSortedFiles, selectedWorklist, allWorklists, mergedHierarchicalTags, mergedTagNameToIdMap]);

  return result;
};
